import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip as RTooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import { Play, Pause, ChevronLeft, ChevronRight, Radio, Cpu, Activity, Target } from "lucide-react";

/**
 * MotionIQStudio — Spatial Fusion Studio (Phase 1 visual build).
 *
 * Layers (bottom → top, rendered inside a single SVG overlay):
 *   1. Perspective floor grid            (3D-look ice-blue lines)
 *   2. Stroboscopic ghost skeletons      (fading trail of last N frames)
 *   3. Trajectory arcs                   (bat_head / wrist_r / head paths)
 *   4. Live current-frame skeleton       (bright cyan bones + amber joints)
 *   5. Kinetic vector arrows             (elbow valgus, hip-shoulder sep,
 *                                        trunk tilt — sport-mode aware)
 *   6. Head tracking dot + drift arc
 *
 * The right rail hosts the live-metric HUD; the bottom rail hosts the
 * kinematic-sequence timing bar and the telemetry graph.
 */

// ── Utilities ─────────────────────────────────────────────────────────
const EVENT_LABEL = {
  leg_lift: "Leg lift", max_knee_lift: "Peak knee", foot_plant: "Foot plant",
  max_external_rotation: "Max ER", release_point: "Release", follow_through: "Follow-through",
  load: "Load", launch: "Launch", heel_strike: "Heel strike", pelvic_peak: "Pelvic peak",
  torso_peak: "Torso peak", hand_launch: "Hand launch", contact: "Contact", extension: "Extension",
  prep_step: "Prep step", first_reaction: "First reaction", crossover_step: "Crossover",
  ball_in_glove: "Ball in glove", throw_release: "Throw release",
  receive: "Receive", transfer: "Transfer", arm_load: "Arm load", release: "Release",
  pop_time_stop: "Pop-time stop", drop_step: "Drop step", first_reaction_cut: "First cut",
  route_apex: "Route apex", read: "Read",
};

// MediaPipe-compatible bone map.
const BONES = [
  ["shoulder_l", "shoulder_r"], ["hip_l", "hip_r"],
  ["shoulder_l", "hip_l"], ["shoulder_r", "hip_r"],
  ["shoulder_l", "elbow_l"], ["elbow_l", "wrist_l"],
  ["shoulder_r", "elbow_r"], ["elbow_r", "wrist_r"],
  ["hip_l", "knee_l"], ["knee_l", "ankle_l"],
  ["hip_r", "knee_r"], ["knee_r", "ankle_r"],
  ["cervical", "head"], ["cervical", "thorax"], ["thorax", "pelvis"],
];

// Metric tiles per sport-mode: which live_telemetry keys we surface in
// the HUD panel + how we display them (unit + colour accent).
const HUD_TILES = {
  pitching: [
    { key: "elbow_valgus_stress", label: "Elbow valgus", unit: "Nm", tone: "danger" },
    { key: "shoulder_ir_velocity", label: "Shoulder IR vel", unit: "°/s", tone: "warn" },
    { key: "hip_shoulder_separation", label: "Hip-shoulder sep", unit: "°", tone: "ok" },
    { key: "trunk_tilt", label: "Trunk tilt", unit: "°", tone: "info" },
    { key: "arm_speed", label: "Arm speed", unit: "°/s", tone: "warn" },
    { key: "stride_length_pct_height", label: "Stride length", unit: "% ht", tone: "ok",
      target: 85, targetLabel: "Fleisig 2022 · target 85%" },
  ],
  hitting: [
    { key: "bat_speed", label: "Bat speed", unit: "mph", tone: "danger" },
    { key: "hand_speed", label: "Hand speed", unit: "mph", tone: "warn" },
    { key: "attack_angle", label: "Attack angle", unit: "°", tone: "ok" },
    { key: "rotational_acceleration", label: "Rot. accel", unit: "g", tone: "warn" },
    { key: "on_plane_efficiency", label: "On-plane", unit: "%", tone: "info" },
    { key: "hip_shoulder_separation", label: "Hip-shoulder sep", unit: "°", tone: "ok" },
    { key: "spine_angle", label: "Spine angle", unit: "°", tone: "info" },
    { key: "posture_retention", label: "Stay in posture", unit: "%", tone: "ok" },
  ],
  infield: [
    { key: "first_step_reaction", label: "First-step react", unit: "s", tone: "danger" },
    { key: "com_displacement", label: "COM drop", unit: "cm", tone: "warn" },
    { key: "shin_angle", label: "Shin angle", unit: "°", tone: "ok" },
    { key: "head_stability", label: "Head stability", unit: "cm", tone: "info" },
  ],
  catcher: [
    { key: "pop_time", label: "Pop time", unit: "s", tone: "danger" },
    { key: "exchange_time", label: "Exchange", unit: "s", tone: "warn" },
    { key: "arm_speed", label: "Arm speed", unit: "°/s", tone: "warn" },
    { key: "head_stability", label: "Head stability", unit: "cm", tone: "info" },
  ],
  outfield: [
    { key: "top_speed", label: "Top speed", unit: "mph", tone: "danger" },
    { key: "route_angle", label: "Route angle", unit: "°", tone: "warn" },
    { key: "first_step_efficiency", label: "First-step eff", unit: "°", tone: "ok" },
    { key: "head_stability", label: "Head stability", unit: "cm", tone: "info" },
  ],
};

// Which events, in order, comprise a kinematic sequence for each mode.
// The Studio renders this as a horizontal timing bar so coaches can see
// pelvis → torso → arm → hand peak ordering at a glance.
const KINEMATIC_SEQUENCE = {
  pitching: ["foot_plant", "max_external_rotation", "release_point", "follow_through"],
  hitting: ["heel_strike", "pelvic_peak", "torso_peak", "hand_launch", "contact"],
  infield: ["prep_step", "first_reaction", "crossover_step", "ball_in_glove"],
  catcher: ["receive", "transfer", "arm_load", "release"],
  outfield: ["read", "drop_step", "route_apex", "ball_in_glove"],
};

const TONE_COLOR = {
  danger: "#F43F5E", warn: "#F59E0B", ok: "#22D3EE", info: "#A78BFA",
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ── Component ─────────────────────────────────────────────────────────
export default function MotionIQStudio({ capture, sportConfig, telemetryEvents = [], initialJumpToMs = 0, onEventJump }) {
  const videoRef = useRef(null);
  const [currentMs, setCurrentMs] = useState(initialJumpToMs || 0);
  const [playing, setPlaying] = useState(false);
  const [frames, setFrames] = useState(capture?.frames || []);
  const [videoDims, setVideoDims] = useState({ w: 1280, h: 720 });
  const [showStrobe, setShowStrobe] = useState(true);

  useEffect(() => {
    setFrames(capture?.frames || []);
    // Snap to the initial jump target (deep-link ?event=...) instead of
    // resetting the scrubber to 0 whenever the capture changes.
    setCurrentMs(initialJumpToMs || 0);
    if (!capture?.capture_id) return;
    (async () => {
      try {
        const { data } = await api.get(`/motioniq/captures/${capture.capture_id}/frames`);
        if (Array.isArray(data?.frames) && data.frames.length > (capture?.frames?.length || 0)) {
          setFrames(data.frames);
        }
      } catch { /* silent */ }
    })();
  }, [capture?.capture_id, initialJumpToMs]);

  // Sync video element to the initial jump target once metadata loads.
  useEffect(() => {
    if (!initialJumpToMs) return;
    const v = videoRef.current;
    if (v && v.readyState >= 1) v.currentTime = initialJumpToMs / 1000;
  }, [initialJumpToMs, capture?.capture_id]);

  // Deep-link listener: any component can dispatch a `motioniq:jump`
  // CustomEvent with { detail: { ms } } to snap this Studio to a frame.
  useEffect(() => {
    const onJump = (e) => {
      const ms = e.detail?.ms ?? 0;
      setCurrentMs(ms);
      const v = videoRef.current;
      if (v) v.currentTime = ms / 1000;
    };
    window.addEventListener("motioniq:jump", onJump);
    return () => window.removeEventListener("motioniq:jump", onJump);
  }, []);

  useEffect(() => {
    if (!playing) return;
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (v) setCurrentMs(Math.round(v.currentTime * 1000));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  // Current frame (nearest by timestamp).
  const currentIdx = useMemo(() => {
    if (!frames.length) return -1;
    let best = 0, bestDelta = Math.abs(frames[0].timestamp_ms - currentMs);
    for (let i = 1; i < frames.length; i++) {
      const d = Math.abs(frames[i].timestamp_ms - currentMs);
      if (d < bestDelta) { best = i; bestDelta = d; }
    }
    return best;
  }, [frames, currentMs]);
  const currentFrame = currentIdx >= 0 ? frames[currentIdx] : null;

  // Stroboscopic ghost trail — every Nth frame within a look-back window
  // ending at currentIdx. Excludes the current frame (drawn bright on top).
  const ghostFrames = useMemo(() => {
    if (!showStrobe || currentIdx < 0) return [];
    const window = 40;                        // frames of look-back
    const stride = 5;                          // every 5th frame
    const start = Math.max(0, currentIdx - window);
    const out = [];
    for (let i = start; i < currentIdx; i += stride) {
      out.push({ frame: frames[i], age: (currentIdx - i) / window });
    }
    return out;
  }, [frames, currentIdx, showStrobe]);

  // Trajectory arcs — collect the anchor joint's pixel positions across
  // the ghost window so we can render a continuous SVG path with a
  // gradient stroke.  Sport-mode decides which joints qualify.
  const trajectoryPaths = useMemo(() => {
    if (currentIdx < 0) return [];
    const window = 60;
    const start = Math.max(0, currentIdx - window);
    const slice = frames.slice(start, currentIdx + 1);
    const mode = capture?.mode || "pitching";
    const anchors = mode === "hitting"
      ? [{ key: "bat_head", color: "#F43F5E", label: "Bat path" },
         { key: "wrist_r",  color: "#22D3EE", label: "Hand path" },
         { key: "head",     color: "#A78BFA", label: "Head drift" }]
      : [{ key: "wrist_r",  color: "#F59E0B", label: "Release arc" },
         { key: "elbow_r",  color: "#F43F5E", label: "Elbow path" },
         { key: "head",     color: "#A78BFA", label: "Head drift" }];
    return anchors.map((a) => {
      const pts = slice
        .map((f) => f?.joint_pixels_2d?.[a.key])
        .filter(Boolean);
      if (pts.length < 2) return null;
      const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
      return { ...a, d };
    }).filter(Boolean);
  }, [frames, currentIdx, capture?.mode]);

  // Telemetry graph data
  const telemetryKey = useMemo(() => {
    const mode = capture?.mode || "pitching";
    const preferred = HUD_TILES[mode]?.[0]?.key;
    return preferred || "torque_nm";
  }, [capture?.mode]);
  const telemetrySeries = useMemo(() => (
    frames.map((f) => ({
      ms: f.timestamp_ms,
      value: Number(f.live_telemetry?.[telemetryKey] || 0),
      event: f.event || null,
    }))
  ), [frames, telemetryKey]);

  const events = capture?.events || [];
  const durationMs = capture?.duration_ms || (frames.at(-1)?.timestamp_ms ?? 5000);
  const sequence = KINEMATIC_SEQUENCE[capture?.mode || "pitching"];
  const hudTiles = HUD_TILES[capture?.mode || "pitching"];

  // Peak stride length across the whole capture — that's the value a
  // coach cares about (foot-strike, not stance). Falls back to the max
  // stride_length_pct_height seen in any frame's joint_angles_3d block.
  const peakStrideLengthPct = useMemo(() => {
    let max = null;
    for (const f of frames) {
      const v = f?.joint_angles_3d?.stride_length_pct_height;
      if (typeof v === "number" && (max == null || v > max)) max = v;
    }
    return max;
  }, [frames]);

  const jumpTo = (ms) => {
    setCurrentMs(ms);
    const v = videoRef.current;
    if (v) v.currentTime = ms / 1000;
  };
  const jumpToEvent = (evt) => {
    jumpTo(evt.timestamp_ms || 0);
    onEventJump?.(evt);
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const stepFrame = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause(); setPlaying(false);
    v.currentTime = clamp(v.currentTime + delta, 0, (durationMs / 1000));
    setCurrentMs(Math.round(v.currentTime * 1000));
  };

  return (
    <section className="rounded-2xl ink-card overflow-hidden" data-testid="motioniq-studio">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <Cpu className="h-3.5 w-3.5 text-fuchsia-400" /> MotionIQ · Spatial Fusion Studio
          </div>
          <div className="mt-1 font-display font-black text-lg text-white tracking-tight">
            {capture?.label || "Capture"}
            <span className="ml-2 text-[10px] font-mono-num tracking-[0.15em] uppercase text-white/40">
              {(sportConfig?.label || capture?.mode || "").toString()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowStrobe((s) => !s)}
            className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border transition-colors ${
              showStrobe ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300"
                : "border-white/15 text-white/50 hover:text-white/80"
            }`}
            data-testid="motioniq-strobe-toggle"
            title="Toggle stroboscopic ghost skeletons"
          >
            Strobe
          </button>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/45 font-mono-num">
            {frames.length} frames · {(durationMs / 1000).toFixed(2)}s
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-12">
        {/* Main stage */}
        <div className="md:col-span-9">
          {/* Video + fused overlay */}
          <div className="relative bg-black" style={{ aspectRatio: "16/9" }} data-testid="motioniq-video-stage">
            <video
              ref={videoRef}
              src={capture?.video_url}
              className="absolute inset-0 w-full h-full object-cover bg-black opacity-40"
              onLoadedMetadata={(e) => {
                setVideoDims({ w: e.currentTarget.videoWidth || 1280, h: e.currentTarget.videoHeight || 720 });
              }}
              onEnded={() => setPlaying(false)}
              playsInline
              muted
              data-testid="motioniq-video"
            />
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${videoDims.w} ${videoDims.h}`}
              preserveAspectRatio="xMidYMid meet"
              data-testid="motioniq-overlay"
            >
              <defs>
                <linearGradient id="gridGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(34,211,238,0.05)" />
                  <stop offset="100%" stopColor="rgba(34,211,238,0.35)" />
                </linearGradient>
                <radialGradient id="stageGlow" cx="50%" cy="80%" r="60%">
                  <stop offset="0%" stopColor="rgba(34,211,238,0.18)" />
                  <stop offset="100%" stopColor="rgba(10,20,36,0)" />
                </radialGradient>
                <filter id="softBlur"><feGaussianBlur stdDeviation="1.2" /></filter>
              </defs>

              {/* Layer 1 — perspective floor grid */}
              <FloorGrid w={videoDims.w} h={videoDims.h} />
              <rect x="0" y="0" width={videoDims.w} height={videoDims.h} fill="url(#stageGlow)" />

              {/* Layer 3 — trajectory arcs (drawn beneath ghosts so bones sit on top) */}
              {trajectoryPaths.map((p) => (
                <g key={p.key} opacity="0.85">
                  <path d={p.d} stroke={p.color} strokeWidth="3" fill="none"
                        strokeLinecap="round" strokeLinejoin="round"
                        strokeDasharray="0" filter="url(#softBlur)" />
                  <path d={p.d} stroke={p.color} strokeWidth="1.5" fill="none"
                        strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
                </g>
              ))}

              {/* Layer 2 — stroboscopic ghost skeletons */}
              {ghostFrames.map(({ frame, age }, i) => (
                <SkeletonBones
                  key={`ghost-${i}`}
                  pts={frame?.joint_pixels_2d}
                  color="#22D3EE"
                  opacity={0.06 + 0.24 * (1 - age)}
                  strokeWidth={2}
                />
              ))}

              {/* Layer 4 — current-frame skeleton */}
              <SkeletonBones
                pts={currentFrame?.joint_pixels_2d}
                color="#22D3EE"
                opacity={1}
                strokeWidth={4}
                showJoints
              />

              {/* Layer 5 — kinetic vector arrows */}
              <KineticVectors
                frame={currentFrame}
                mode={capture?.mode}
                vectors={sportConfig?.primary_vectors}
              />

              {/* Layer 6 — head tracking dot + drift arc */}
              <HeadTracker frames={frames} currentIdx={currentIdx} />

              {/* Trajectory legend */}
              {trajectoryPaths.length > 0 && (
                <g transform={`translate(${videoDims.w - 200}, 24)`}>
                  <rect x="0" y="0" width="190" height={20 + trajectoryPaths.length * 18}
                        rx="8" fill="rgba(10,20,36,0.7)" stroke="rgba(255,255,255,0.08)" />
                  {trajectoryPaths.map((p, i) => (
                    <g key={p.key} transform={`translate(10, ${18 + i * 18})`}>
                      <line x1="0" y1="0" x2="22" y2="0" stroke={p.color} strokeWidth="3" strokeLinecap="round" />
                      <text x="30" y="4" fill="rgba(255,255,255,0.85)"
                            fontSize="11" fontFamily="monospace" fontWeight="700">
                        {p.label}
                      </text>
                    </g>
                  ))}
                </g>
              )}

              {/* Pose-pending banner */}
              {(!currentFrame || !currentFrame.joint_pixels_2d || Object.keys(currentFrame.joint_pixels_2d || {}).length === 0) && (
                <g opacity="0.9">
                  <rect x="40" y="40" width="300" height="34" rx="8" fill="rgba(0,0,0,0.6)" />
                  <text x="52" y="62" fill="#F59E0B" fontSize="13" fontFamily="monospace" fontWeight="700">
                    POSE DATA PENDING · SEED DEMO OR RUN PHASE 2 ML
                  </text>
                </g>
              )}
            </svg>

            {/* Event marker rail */}
            {events.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-2 bg-white/10" data-testid="motioniq-event-rail">
                {events.map((ev) => (
                  <button
                    key={`${ev.event}-${ev.frame_id}`}
                    onClick={() => jumpToEvent(ev)}
                    className="absolute top-0 h-full w-[2px] bg-red-500 hover:w-[3px] transition-all"
                    style={{ left: `${(ev.timestamp_ms / durationMs) * 100}%` }}
                    title={`${EVENT_LABEL[ev.event] || ev.event} · ${ev.timestamp_ms}ms`}
                    data-testid={`event-marker-${ev.event}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Transport */}
          <div className="px-5 py-3 border-b border-[var(--ink-border)] flex items-center gap-3">
            <Button size="sm" variant="ghost" onClick={() => stepFrame(-1 / (capture?.video_fps || 60))}
              className="text-white/70 hover:text-white" data-testid="motioniq-step-back">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={togglePlay}
              className="text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="motioniq-play-toggle">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => stepFrame(1 / (capture?.video_fps || 60))}
              className="text-white/70 hover:text-white" data-testid="motioniq-step-fwd">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <input
              type="range"
              min={0}
              max={durationMs}
              step={16}
              value={currentMs}
              onChange={(e) => jumpTo(parseInt(e.target.value, 10))}
              className="flex-1 accent-red-500"
              data-testid="motioniq-scrubber"
            />
            <span className="text-[11px] font-mono-num text-white/60 tabular-nums w-24 text-right">
              {(currentMs / 1000).toFixed(2)}s / {(durationMs / 1000).toFixed(2)}s
            </span>
          </div>

          {/* Kinematic sequence bar */}
          {events.length > 0 && (
            <div className="px-5 py-3 border-b border-[var(--ink-border)]" data-testid="motioniq-kinematic-sequence">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-2 flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5 text-fuchsia-400" /> Kinematic sequence
              </div>
              <KinematicSequenceBar
                events={events}
                sequence={sequence}
                durationMs={durationMs}
                currentMs={currentMs}
                onJump={(ms) => jumpTo(ms)}
              />
            </div>
          )}

          {/* Telemetry graph */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
                <Radio className="h-3.5 w-3.5" style={{ color: "var(--brand-secondary)" }} />
                Live telemetry · {telemetryKey.replace(/_/g, " ")}
              </div>
              {currentFrame && (
                <div className="text-[11px] font-mono-num text-white/60">
                  t={currentFrame.timestamp_ms}ms · frame={currentFrame.frame_id}
                </div>
              )}
            </div>
            <div style={{ height: 140 }} data-testid="motioniq-telemetry-graph">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={telemetrySeries} margin={{ top: 6, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="ms" stroke="#64748B" tick={{ fill: "#64748B", fontSize: 10 }}
                    tickFormatter={(v) => `${(v / 1000).toFixed(1)}s`} />
                  <YAxis stroke="#64748B" tick={{ fill: "#64748B", fontSize: 10 }} width={40} />
                  <RTooltip contentStyle={{ background: "#0A1424", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }}
                    labelFormatter={(v) => `${v}ms`} />
                  <Line type="monotone" dataKey="value" stroke="var(--brand-secondary)"
                        strokeWidth={2} dot={false} isAnimationActive={false} />
                  <ReferenceLine x={currentMs} stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="4 4" />
                  {events.map((ev) => (
                    <ReferenceLine key={`ref-${ev.frame_id}`} x={ev.timestamp_ms} stroke="rgba(217,4,41,0.35)" />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* HUD panel */}
        <aside className="md:col-span-3 border-l border-[var(--ink-border)] p-4 space-y-3" data-testid="motioniq-hud">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5 text-fuchsia-400" /> Live metrics
          </div>
          <div className="space-y-2">
            {hudTiles.map((tile) => {
              // Stride length is derived client-side from joint_angles_3d
              // (populated by PoseExtractor). Fall back to live_telemetry
              // if the coach pushed a manually measured value.
              const raw = currentFrame?.live_telemetry?.[tile.key];
              const derived = tile.key === "stride_length_pct_height"
                ? peakStrideLengthPct
                : (currentFrame?.joint_angles_3d?.[tile.key] ?? null);
              const value = raw != null ? raw : derived;
              return (
                <MetricTile
                  key={tile.key}
                  label={tile.label}
                  unit={tile.unit}
                  tone={tile.tone}
                  value={value}
                  target={tile.target}
                  targetLabel={tile.targetLabel}
                />
              );
            })}
          </div>

          {/* Event chips */}
          {events.length > 0 && (
            <div className="pt-2" data-testid="motioniq-event-chips">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-2">Events</div>
              <div className="flex flex-wrap gap-1.5">
                {events.map((ev) => {
                  const active = currentFrame?.event === ev.event;
                  return (
                    <button
                      key={`chip-${ev.event}-${ev.frame_id}`}
                      onClick={() => jumpToEvent(ev)}
                      className={`text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-1 rounded-full border transition-colors ${
                        active ? "bg-red-500/20 border-red-500 text-red-300"
                          : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                      }`}
                      data-testid={`event-chip-${ev.event}`}
                    >
                      {EVENT_LABEL[ev.event] || ev.event}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* External telemetry (Blast / HitTrax) */}
          {telemetryEvents.length > 0 && (
            <div className="pt-3" data-testid="motioniq-external-telemetry">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-2">
                External telemetry
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                {telemetryEvents.slice(0, 8).map((ev) => {
                  const isBlast = ev.source === "blast_motion";
                  const primary = isBlast
                    ? (ev.metrics?.bat_speed_mph ? `${ev.metrics.bat_speed_mph} mph` : "swing")
                    : (ev.metrics?.exit_velocity_mph ? `${ev.metrics.exit_velocity_mph} EV` : "hit");
                  return (
                    <div key={ev.event_id}
                      className="rounded-md border border-white/8 bg-white/[0.02] px-2 py-1.5"
                      data-testid={`external-tev-${ev.event_id}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-[9px] font-bold uppercase tracking-[0.15em] ${
                          isBlast ? "text-fuchsia-300" : "text-emerald-300"
                        }`}>
                          {isBlast ? "Blast" : "HitTrax"}
                        </span>
                        <span className="font-mono-num text-[11px] text-white">{primary}</span>
                      </div>
                      <div className="text-[9px] text-white/40 font-mono-num mt-0.5">
                        {(ev.timestamp || "").slice(0, 19).replace("T", " ")}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Joint readout */}
      {currentFrame?.joint_angles_3d && Object.keys(currentFrame.joint_angles_3d).length > 0 && (
        <div className="px-5 pb-5 pt-2 border-t border-[var(--ink-border)]" data-testid="motioniq-joint-readout">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-2">
            Joint vectors · this frame
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {Object.entries(currentFrame.joint_angles_3d).slice(0, 12).map(([k, v]) => (
              <div key={k}
                className="rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2"
                data-testid={`joint-${k}`}
              >
                <div className="text-[9px] tracking-[0.2em] uppercase text-white/40">{k.replace(/_/g, " ")}</div>
                <div className="mt-0.5 font-mono-num text-white text-sm">
                  {typeof v === "number" ? v.toFixed(1) : v}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Sub-components ────────────────────────────────────────────────────
function MetricTile({ label, unit, tone, value, target, targetLabel }) {
  const color = TONE_COLOR[tone] || "#22D3EE";
  const has = typeof value === "number";
  const hitsTarget = has && typeof target === "number" && value >= target;
  return (
    <div
      className="rounded-lg border px-3 py-2 flex items-center justify-between"
      style={{ borderColor: hitsTarget ? "rgba(16,185,129,0.55)" : "rgba(255,255,255,0.08)", background: hitsTarget ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.02)" }}
      data-testid={`hud-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div>
        <div className="text-[9px] tracking-[0.2em] uppercase text-white/40">{label}</div>
        <div className="mt-0.5 font-mono-num text-white text-base tabular-nums">
          {has ? value.toFixed(1) : "—"}
          <span className="text-[10px] text-white/40 ml-1">{unit}</span>
          {typeof target === "number" && (
            <span className="text-[10px] text-white/40 ml-2">
              / target {target}{unit}
            </span>
          )}
        </div>
        {targetLabel && (
          <div className="text-[9px] text-white/35 mt-0.5">{targetLabel}</div>
        )}
      </div>
      <span className="h-2 w-2 rounded-full" style={{ background: hitsTarget ? "#10B981" : color, boxShadow: `0 0 12px ${hitsTarget ? "#10B981" : color}` }} />
    </div>
  );
}

// Perspective floor grid: ice-blue lines converging to a vanishing
// point near the top-center of the frame.
function FloorGrid({ w, h }) {
  const horizon = h * 0.42;
  const vpX = w * 0.5;
  const verticals = 11;
  const horizontals = 7;
  const lines = [];
  for (let i = 0; i <= verticals; i++) {
    const t = i / verticals;
    const bottomX = t * w;
    lines.push(
      <line key={`v-${i}`} x1={bottomX} y1={h} x2={vpX} y2={horizon}
            stroke="url(#gridGrad)" strokeWidth="1" />
    );
  }
  for (let i = 1; i <= horizontals; i++) {
    // Move each horizontal further from the bottom, with a non-linear
    // ease so they compress near the horizon like real perspective.
    const p = Math.pow(i / horizontals, 1.5);
    const y = h - (h - horizon) * p;
    const shrink = 0.02 + 0.98 * (1 - p);
    const x1 = w * 0.5 - (w * 0.5) * shrink;
    const x2 = w * 0.5 + (w * 0.5) * shrink;
    lines.push(
      <line key={`h-${i}`} x1={x1} y1={y} x2={x2} y2={y}
            stroke="rgba(34,211,238,0.28)" strokeWidth="1" />
    );
  }
  // Horizon line
  lines.push(
    <line key="horizon" x1="0" y1={horizon} x2={w} y2={horizon}
          stroke="rgba(34,211,238,0.15)" strokeWidth="1" strokeDasharray="4 6" />
  );
  return <g opacity="0.9">{lines}</g>;
}

function SkeletonBones({ pts, color = "#22D3EE", opacity = 1, strokeWidth = 3, showJoints = false }) {
  if (!pts || Object.keys(pts).length === 0) return null;
  return (
    <g opacity={opacity}>
      {BONES.map(([a, b]) => {
        const pa = pts[a], pb = pts[b];
        if (!pa || !pb) return null;
        return (
          <line key={`${a}-${b}`}
            x1={pa[0]} y1={pa[1]} x2={pb[0]} y2={pb[1]}
            stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          />
        );
      })}
      {showJoints && Object.entries(pts).map(([k, [x, y]]) => (
        <circle key={k} cx={x} cy={y} r={5} fill="#F59E0B" stroke="#0A1424" strokeWidth="2" />
      ))}
      {/* Bat as a distinct highlight when both bat_knob and bat_head exist */}
      {showJoints && pts.bat_knob && pts.bat_head && (
        <line
          x1={pts.bat_knob[0]} y1={pts.bat_knob[1]}
          x2={pts.bat_head[0]} y2={pts.bat_head[1]}
          stroke="#F43F5E" strokeWidth="5" strokeLinecap="round"
        />
      )}
    </g>
  );
}

// Kinetic vector arrows — anchor to the joint that best represents the
// stress point for each named vector, then draw an arrow whose length
// scales with the live_telemetry value.
function KineticVectors({ frame, mode, vectors = [] }) {
  if (!frame?.joint_pixels_2d) return null;
  const pts = frame.joint_pixels_2d;
  const telem = frame.live_telemetry || {};

  const specs = mode === "hitting"
    ? [
        { key: "attack_angle",   anchor: pts.bat_head || pts.wrist_r, label: "Attack°",
          color: "#22D3EE", scale: 3 },
        { key: "spine_angle", anchor: pts.thorax, label: "Spine°",
          color: "#F59E0B", scale: 2.5 },
        { key: "posture_retention", anchor: pts.head, label: "Posture%",
          color: "#22D3EE", scale: 0.8, dy: -20 },
        { key: "bat_speed", anchor: pts.bat_head || pts.wrist_r, label: "Bat mph",
          color: "#F43F5E", scale: 1.5, dy: 24 },
      ]
    : [
        { key: "elbow_valgus_stress", anchor: pts.elbow_r, label: "Valgus Nm",
          color: "#F43F5E", scale: 1.2 },
        { key: "shoulder_ir_velocity", anchor: pts.shoulder_r, label: "IR °/s",
          color: "#F59E0B", scale: 0.02 },
        { key: "hip_shoulder_separation", anchor: pts.pelvis, label: "Hip-Sh sep°",
          color: "#A78BFA", scale: 2 },
      ];

  return (
    <g>
      {specs.map((s, i) => {
        if (!s.anchor) return null;
        const val = Number(telem[s.key] || 0);
        const len = clamp(20 + val * s.scale, 20, 220);
        const dy = s.dy || 0;
        const x1 = s.anchor[0], y1 = s.anchor[1];
        const x2 = x1 + 60, y2 = y1 - 30 - i * 34 + dy;
        return (
          <g key={s.key}>
            <line x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={s.color} strokeWidth="2" strokeDasharray="4 3" opacity="0.85" />
            <line x1={x2} y1={y2} x2={x2 + len} y2={y2}
                  stroke={s.color} strokeWidth="3" strokeLinecap="round" />
            <polygon
              points={`${x2 + len},${y2} ${x2 + len - 8},${y2 - 5} ${x2 + len - 8},${y2 + 5}`}
              fill={s.color}
            />
            <text x={x2} y={y2 - 6} fill={s.color}
                  fontSize="11" fontFamily="monospace" fontWeight="700">
              {s.label} {val ? val.toFixed(1) : ""}
            </text>
          </g>
        );
      })}
    </g>
  );
}

// Head tracker: bright dot at current head pixel + a trailing arc drawn
// over the recent head-pixel history.
function HeadTracker({ frames, currentIdx }) {
  if (currentIdx < 0) return null;
  const window = 90;
  const start = Math.max(0, currentIdx - window);
  const slice = frames.slice(start, currentIdx + 1);
  const pts = slice
    .map((f) => f?.joint_pixels_2d?.head)
    .filter(Boolean);
  if (pts.length === 0) return null;
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const head = pts[pts.length - 1];
  return (
    <g>
      <path d={d} stroke="rgba(167,139,250,0.7)" strokeWidth="2" fill="none"
            strokeLinecap="round" strokeDasharray="2 3" />
      <circle cx={head[0]} cy={head[1]} r="10"
              fill="none" stroke="#A78BFA" strokeWidth="2" opacity="0.6" />
      <circle cx={head[0]} cy={head[1]} r="4" fill="#A78BFA" />
    </g>
  );
}

function KinematicSequenceBar({ events, sequence, durationMs, currentMs, onJump }) {
  // Only show events that are part of the mode's kinematic sequence.
  const seq = sequence
    .map((name) => events.find((e) => e.event === name))
    .filter(Boolean);
  if (seq.length === 0) return (
    <div className="text-[11px] text-white/40 font-mono-num">No kinematic markers on this capture.</div>
  );
  return (
    <div className="relative h-14">
      <div className="absolute top-6 left-0 right-0 h-1 rounded-full bg-white/8" />
      <div
        className="absolute top-6 left-0 h-1 rounded-full"
        style={{
          width: `${clamp((currentMs / durationMs) * 100, 0, 100)}%`,
          background: "linear-gradient(90deg, #F43F5E, #F59E0B, #22D3EE)",
        }}
      />
      {seq.map((ev, i) => {
        const pct = clamp((ev.timestamp_ms / durationMs) * 100, 0, 100);
        const active = currentMs >= ev.timestamp_ms - 40;
        return (
          <button
            key={`seq-${ev.event}`}
            onClick={() => onJump(ev.timestamp_ms)}
            className="absolute -translate-x-1/2 group"
            style={{ left: `${pct}%`, top: 0 }}
            data-testid={`sequence-node-${ev.event}`}
          >
            <div className="flex flex-col items-center">
              <div className={`text-[9px] font-bold uppercase tracking-[0.1em] mb-1 ${
                active ? "text-white" : "text-white/40 group-hover:text-white/70"
              }`}>
                {i + 1}. {EVENT_LABEL[ev.event] || ev.event}
              </div>
              <span
                className={`block h-4 w-4 rounded-full border-2 transition-all ${
                  active ? "bg-amber-400 border-white" : "bg-white/10 border-white/25"
                }`}
              />
              <div className="text-[9px] font-mono-num text-white/40 mt-1">{ev.timestamp_ms}ms</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
